import { useCallback, useEffect, useRef, useState } from "react";

import { MedplumClient } from "@medplum/core";
import { Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { useForm } from "@mantine/form";

import { zodResolver } from "mantine-form-zod-resolver";
import { ZodObject, ZodRawShape } from "zod";

import {
  normalizePostpartumVitalsQuestionnaire,
  seedDefaultPostpartumVitalsTemperatureUnit,
} from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/utils/normalize-postpartum-vitals-questionnaire";
import { getQuestionnaire } from "@/encounters/components/EncounterLabor/services/labor";
import { hydrateLaborFlowsheetDateTimeFormValuesForEdit } from "@/encounters/components/EncounterLabor/utils/form-datetime";
import { Extensions } from "@/fhir/enums/extensions";
import { createFormInitialValues } from "@/intake-forms/utils/intake-forms";
import { getQuestionnaireSchema } from "@/utils/questionnaires";

interface UseLaborQuestionnaireFormProps {
  key?: number;
  questionnaireName: string | null;
  encounterId?: string;
  questionnaireId?: string;
  questionnaireResponse?: QuestionnaireResponse;
  editMode?: boolean;
  refreshTrigger?: number; // Add refresh trigger to force re-fetch
}

interface UseLaborQuestionnaireFormResult {
  isLoading: boolean;
  questionnaire: Questionnaire | null;
  questionnaireResponse: QuestionnaireResponse | undefined;
  formSchema: ZodObject<ZodRawShape> | undefined;
  form: ReturnType<typeof useForm>;
  questionnaireError: string | null;
  /** FHIR valueDate (YYYY-MM-DD) per DateTime group — pass to labor drawer for time display */
  dateTimeUtcDateAnchors: Record<string, string>;
}

export function useLaborQuestionnaireForm({
  key,
  questionnaireName,
  encounterId,
  questionnaireId,
  questionnaireResponse: initialQuestionnaireResponse,
  editMode = false,
  refreshTrigger,
}: UseLaborQuestionnaireFormProps): UseLaborQuestionnaireFormResult {
  const [isLoading, setIsLoading] = useState(true);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [questionnaireResponse, setQuestionnaireResponse] = useState<
    QuestionnaireResponse | undefined
  >(initialQuestionnaireResponse);
  const [formSchema, setFormSchema] = useState<ZodObject<ZodRawShape> | undefined>();
  const [questionnaireError, setQuestionnaireError] = useState<string | null>(null);
  const [dateTimeUtcDateAnchors, setDateTimeUtcDateAnchors] = useState<Record<string, string>>({});

  const medplum = useMedplum();

  const form = useForm({
    initialValues: {},
    validate: formSchema ? zodResolver(formSchema) : undefined,
  });

  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    setQuestionnaireResponse(initialQuestionnaireResponse);
  }, [initialQuestionnaireResponse]);

  // Helper function to fetch the questionnaire data
  const fetchQuestionnaireData = useCallback(
    async (medplum: MedplumClient, questionnaireName: string) => {
      try {
        // Bypass cache when refreshTrigger is present to get fresh data
        const bypassCache = !!refreshTrigger;

        if (bypassCache) {
          // Try to clear Medplum cache first
          try {
            // Clear any cached questionnaire data
            await medplum.invalidateSearches("Questionnaire");
          } catch (cacheError) {
            // Cache clear failed, continue with fetch
          }
        }

        const questionnaire = await getQuestionnaire(medplum, questionnaireName, bypassCache);
        return questionnaire;
      } catch (error) {
        console.error("Error fetching labor flowsheet form:", questionnaireName, error);
        setQuestionnaireError("Could not load form. Please try again.");
        return null;
      }
    },
    [setQuestionnaireError, refreshTrigger],
  );

  // Helper function to process the fetched questionnaire response
  const processFetchedQuestionnaire = useCallback(
    (savedQuestionnaire: Questionnaire) => {
      // For individual forms, ensure we remove any hidden extensions that might have been added
      let cleanQuestionnaire = savedQuestionnaire;
      if (savedQuestionnaire.item?.[0]?.item) {
        cleanQuestionnaire = {
          ...savedQuestionnaire,
          item: [
            {
              ...savedQuestionnaire.item[0],
              item: savedQuestionnaire.item[0].item.map((item) => {
                // Remove any hidden extensions for individual forms
                const cleanExtensions = item.extension?.filter(
                  (ext) => ext.url !== Extensions.QuestionnaireItemHidden,
                );
                return {
                  ...item,
                  extension:
                    cleanExtensions && cleanExtensions.length > 0 ? cleanExtensions : undefined,
                };
              }),
            },
          ],
        };
      }

      const questionnaire = normalizePostpartumVitalsQuestionnaire(cleanQuestionnaire);
      setQuestionnaire(questionnaire);

      // Get the schema for the questionnaire
      const schema = getQuestionnaireSchema(questionnaire?.item?.[0].item || []);
      setFormSchema(schema);

      // Reset form and initialize with new values
      formRef.current.reset();
      const responseItems = editMode ? questionnaireResponse?.item?.[0].item || [] : [];
      let initialValues = createFormInitialValues(
        questionnaire?.item?.[0].item ?? [],
        responseItems,
      );
      if (editMode && questionnaireResponse) {
        const hydrated = hydrateLaborFlowsheetDateTimeFormValuesForEdit(
          questionnaire,
          initialValues,
          questionnaireResponse,
        );
        initialValues = hydrated.values;
        setDateTimeUtcDateAnchors(hydrated.dateTimeUtcDateAnchors);
      } else {
        setDateTimeUtcDateAnchors({});
      }

      formRef.current.setValues(
        seedDefaultPostpartumVitalsTemperatureUnit(questionnaire, initialValues),
      );
    },
    [questionnaireResponse, editMode],
  );

  useEffect(() => {
    if (!questionnaireName) {
      console.error("Labor flowsheet form name is missing.");
      setQuestionnaireError("Form configuration is missing.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const fetchAndProcess = async () => {
      const savedQuestionnaire = await fetchQuestionnaireData(medplum, questionnaireName);
      if (savedQuestionnaire) {
        processFetchedQuestionnaire(savedQuestionnaire);
      } else if (savedQuestionnaire === undefined) {
        console.error("Labor flowsheet form not found:", questionnaireName);
        setQuestionnaireError("Form configuration not found.");
      }
      setIsLoading(false);
    };

    fetchAndProcess();
  }, [
    medplum,
    questionnaireName,
    encounterId,
    questionnaireId,
    questionnaireResponse,
    editMode,
    processFetchedQuestionnaire,
    fetchQuestionnaireData,
    key,
    refreshTrigger, // Add refreshTrigger to dependencies
  ]);

  return {
    isLoading,
    questionnaire,
    questionnaireResponse,
    formSchema,
    form,
    questionnaireError,
    dateTimeUtcDateAnchors,
  };
}
